<script setup lang="ts">
import { apps, formatting, useContext, useDialogs, useLocalization, useOrderViewContext } from "@hantera/portal-app";
import { IconRedo, IconUndo, IconWarning, Spinner } from "@hantera/design-system";
import { onKeydown } from "@/composition/keyboard";
import useApps from "@/composition/use-apps";
import { computed } from "vue";

const orderViewContext = useOrderViewContext();
const t = useLocalization('components.order.actions');
const tCommon = useLocalization('common');
const dialogs = useDialogs()
const context = useContext()
const appsContext = useApps()


const isInTimetravel = computed(() => orderViewContext.timetravelState.isActive);

onKeydown(e => {
  if (e.key === 'z' && e.ctrlKey) {
    e.preventDefault();
    undo();
  }
  if (e.key === 'y' && e.ctrlKey) {
    e.preventDefault();
    redo();
  }
  if (e.key === 's' && e.ctrlKey) {
    e.preventDefault();
    save();
  }
});

const displaySpinner = computed(() => {
  return orderViewContext.workState.isSaving || orderViewContext.workState.isRefreshing;
});

const spinnerTitle = computed(() => {
  if (orderViewContext.workState.isSaving) {
    return 'Saving...';
  }

  if (orderViewContext.workState.isRefreshing) {
    return 'Refreshing...';
  }
});

async function undo() {
  if (orderViewContext.undo()) {
    await orderViewContext.refresh();
  }
}

async function redo() {
  if (orderViewContext.redo()) {
    await orderViewContext.refresh();
  }
}

async function save() {
  const appliedCommands = await orderViewContext.save();

  if (appliedCommands.length === 0) {
    return;
  }

  for (const provider of appsContext.getServices(apps.standardServices.orderView.eventsProvider)) {
    try {
      await provider.onOrderSaved?.({
        context,
        orderViewContext,
        order: orderViewContext.state,
        appliedCommands
      });
    } catch (e) {
      console.error('An order view eventsProvider.onOrderSaved handler threw:', e);
    }
  }
}


async function exitTimetravel() {
  await orderViewContext.exitTimetravel();
}

async function commitTimetravel() {
  if (await dialogs.confirm('Are you sure you want to revert the order to this checkpoint? Note that payments and external systems will not revert, and this may lead to inconsistencies in state between systems.', 'Are you sure?', undefined, tCommon('cancel'), true)) {
    await orderViewContext.commitTimetravel();
  }
}

</script>

<template>
  <div class="header__actions">

    <Spinner class="header__spinner" :style="!displaySpinner ? 'visibility: hidden' : ''" :title="spinnerTitle" />

    <!-- Timetravel mode buttons -->
    <template v-if="isInTimetravel">
      <div class="timetravel-warning">
        <IconWarning />️ Viewing checkpoint from {{ formatting.dateTime(orderViewContext.timetravelState.timestamp!) }}
      </div>

      <button class="btn" @click="exitTimetravel">
        Close Preview
      </button>

      <button class="btn-danger" @click="commitTimetravel">
        Revert
      </button>
    </template>

    <!-- Normal mode buttons -->
    <template v-else>
      <button class="btn-primary" :disabled="!orderViewContext.historyState.hasPending" @click="save" title="CTRL+S">
        {{ t('save') }}
      </button>

      <div class="btn-group">
        <button class="button--icon" :disabled="!orderViewContext.historyState.hasPending" @click="undo"
          :title="`${t('undo')} (CTRL+Z)`">
          <IconUndo />
        </button>
        <button class="button--icon" :disabled="!orderViewContext.historyState.canRedo" @click="redo"
          :title="`${t('redo')} (CTRL+Y)`">
          <IconRedo />
        </button>
      </div>
    </template>

  </div>
</template>

<style scoped>
.header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.header__spinner {
  width: 22px;
  height: 22px;
  vertical-align: middle;
}

.header__actions :deep(svg) {
  width: 14px;
  height: 14px;
}

.button--icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.timetravel-warning {
  padding: 0.5rem 1rem;
  background-color: var(--color-warning-100);
  border: 1px solid var(--color-warning-25);
  border-radius: 4px;
  color: var(--color-warning-0);
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
</style>
